import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutation, useQuery } from "@apollo/client/react";
import { Send } from "lucide-react";
import { Button, Heading, Mono, Tabs, TabsContent, TabsList, TabsTrigger, Text } from "@athena/ui";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PushNotificationComposer } from "@/components/pushNotifications/PushNotificationComposer";
import { PushNotificationHistory } from "@/components/pushNotifications/PushNotificationHistory";
import {
    draftMessage,
    EMPTY_DRAFT,
    isDraftReady,
    type PushNotificationDraft,
} from "@/components/pushNotifications/pushNotificationDraft";
import {
    ClientPushAudienceDocument,
    ClientPushNotificationHistoryDocument,
    SendClientPushNotificationDocument,
} from "@/graphql/pushNotifications.generated";
import { AudienceFilters } from "./components/AudienceFilters";
import { LeftOutList } from "./components/LeftOutList";
import { RecipientList } from "./components/RecipientList";
import {
    audienceFilterSummary,
    filterChoices,
    isFiltered,
    matchesAudienceFilter,
    type AudienceFilter,
} from "./helpers/audienceFilter";
import { toClientRecord, type ClientRecord } from "./helpers/client";

type Tab = "send" | "history";

/**
 * Push notifications from the alumni console: the admin portal's composer and history, sent only to
 * the alumni of the client organization — never to a Release U, MANUAL or Grace learner.
 *
 * Who it reaches is the server's answer (`clientPushAudience`), by the same rule the send uses: anyone
 * marked do not contact, or with no device to send to, is left out and listed. Everyone else starts
 * checked, as on the broadcast email: uncheck people to leave them out, or use select all and then
 * check one person to send to them alone. The send names exactly the people still checked; the
 * server can only drop someone from that, never add.
 */
export function AlumniPushNotificationsPage() {
    const { t } = useTranslation();
    const ids = useId();
    const [
        tab,
        setTab,
    ] = useState<Tab>("send");
    const [
        draft,
        setDraft,
    ] = useState<PushNotificationDraft>(EMPTY_DRAFT);
    const [
        excluded,
        setExcluded,
    ] = useState<ReadonlySet<string>>(new Set<string>());
    const [
        filter,
        setFilter,
    ] = useState<AudienceFilter>({});
    const [
        confirming,
        setConfirming,
    ] = useState<boolean>(false);

    const { data, loading, error, refetch } = useQuery(ClientPushAudienceDocument);
    const {
        data: historyData,
        loading: historyLoading,
        refetch: refetchHistory,
    } = useQuery(ClientPushNotificationHistoryDocument);
    const [
        sendPush,
        { loading: sending },
    ] = useMutation(SendClientPushNotificationDocument);

    const everyone = useMemo(() => {
        const lists = data?.clientPushAudience;
        return {
            recipients: (lists?.recipients ?? []).map(toClientRecord),
            doNotContact: (lists?.doNotContact ?? []).map(toClientRecord),
            notificationsOff: (lists?.notificationsOff ?? []).map(toClientRecord),
        };
    }, [data]);
    const allAlumni = useMemo(
        () => [
            ...everyone.recipients,
            ...everyone.doNotContact,
            ...everyone.notificationsOff,
        ],
        [everyone],
    );
    // Choices come from everyone, so picking one never takes the others away.
    const locations = useMemo(() => filterChoices(allAlumni.map((client) => client.location)), [allAlumni]);
    const sexes = useMemo(() => filterChoices(allAlumni.map((client) => client.sex)), [allAlumni]);

    const audience = useMemo(() => {
        const matching = (clients: ClientRecord[]) => clients.filter((client) => matchesAudienceFilter(client, filter));
        return {
            recipients: matching(everyone.recipients),
            doNotContact: matching(everyone.doNotContact),
            notificationsOff: matching(everyone.notificationsOff),
        };
    }, [
        everyone,
        filter,
    ]);

    const chosen = audience.recipients.filter((client) => !excluded.has(client.id));
    const recipients = chosen.length;
    const removed = audience.recipients.length - recipients;
    const leftOut = audience.doNotContact.length + audience.notificationsOff.length;
    const filtered = isFiltered(filter);
    const outsideFilters = allAlumni.length - audience.recipients.length - leftOut;
    const failed = Boolean(error) && !data;
    const sentCount = historyData?.clientPushNotificationHistory.length ?? 0;
    const canSend = Boolean(data) && recipients > 0 && isDraftReady(draft);

    const toggle = (clientIds: string[], included: boolean) =>
        setExcluded((current) => {
            const next = new Set(current);
            for (const clientId of clientIds) {
                if (included) next.delete(clientId);
                else next.add(clientId);
            }
            return next;
        });

    const send = async () => {
        let result;
        try {
            const response = await sendPush({
                variables: {
                    input: { ...draftMessage(draft), recipientClientIds: chosen.map((client) => client.id) },
                },
            });
            result = response.data?.sendClientPushNotification;
        } catch {
            toast.error(t("admin.alumni.push.sendFailed"));
            return;
        }
        setConfirming(false);
        if (!result) return;

        // The server sends to whoever is still eligible; anyone else on the list was skipped.
        const skippedCount = recipients - result.recipientCount;
        toast.success(t("admin.alumni.push.sent", { count: result.recipientCount }), {
            description: skippedCount > 0 ? t("admin.alumni.push.skipped", { count: skippedCount }) : undefined,
        });
        // Sent, so a second press would send it again: start over, from who can be reached now.
        setDraft(EMPTY_DRAFT);
        setExcluded(new Set<string>());
        setFilter({});
        void refetch();
        void refetchHistory();
    };

    const confirmDescription = [
        t("admin.alumni.push.confirmDescription", { count: recipients, title: draft.title }),
        filtered
            ? t("admin.alumni.broadcast.confirmFiltered", {
                  count: outsideFilters,
                  filters: audienceFilterSummary(filter, t),
              })
            : null,
        leftOut > 0 ? t("admin.alumni.push.confirmLeftOut", { count: leftOut }) : null,
        removed > 0 ? t("admin.alumni.broadcast.confirmRemoved", { count: removed }) : null,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className="flex flex-col gap-6 px-4 py-6 md:px-10 md:py-8">
            <header className="max-w-2xl">
                <Heading level={2} as="h1">
                    {t("admin.alumni.push.title")}
                </Heading>
                <Text size="sm" muted className="mt-1">
                    {t("admin.alumni.push.description")}
                </Text>
            </header>

            <Tabs value={tab} onValueChange={(value) => setTab(value as Tab)}>
                <TabsList variant="line" className="border-border mb-6 border-b">
                    <TabsTrigger
                        variant="line"
                        value="send"
                        className="data-[state=active]:border-secondary data-[state=active]:text-secondary"
                    >
                        {t("admin.alumni.push.sendTab")}
                    </TabsTrigger>
                    <TabsTrigger
                        variant="line"
                        value="history"
                        className="data-[state=active]:border-secondary data-[state=active]:text-secondary"
                    >
                        {t("admin.alumni.push.historyTab", { count: sentCount })}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="send" className="mt-0 space-y-4">
                    <PushNotificationComposer draft={draft} onChange={setDraft} showDeepLinkConfig={false} />

                    <section aria-labelledby={`${ids}-recipients`} className="border-border rounded-2xl border">
                        <div className="bg-card-background border-border rounded-t-2xl border-b px-5 py-3">
                            <Mono
                                id={`${ids}-recipients`}
                                className="text-muted-foreground text-xs tracking-widest uppercase"
                            >
                                {t("admin.alumni.push.recipientsTitle")}
                            </Mono>
                        </div>
                        <div className="flex flex-col gap-5 px-5 py-4">
                            {failed ? (
                                <Text size="sm" muted>
                                    {t("admin.alumni.push.loadFailed")}
                                </Text>
                            ) : (
                                <>
                                    <AudienceFilters
                                        filter={filter}
                                        onChange={setFilter}
                                        locations={locations}
                                        sexes={sexes}
                                    />
                                    {leftOut > 0 ? (
                                        <div>
                                            <Text size="sm" weight="semibold">
                                                {t("admin.alumni.push.leftOutTitle")}
                                            </Text>
                                            <Text size="xs" muted className="mt-1 mb-3">
                                                {t("admin.alumni.push.leftOutDescription")}
                                            </Text>
                                            <LeftOutList
                                                groups={[
                                                    {
                                                        label: t("admin.alumni.broadcast.doNotContact"),
                                                        clients: audience.doNotContact,
                                                    },
                                                    {
                                                        label: t("admin.alumni.push.notificationsOff"),
                                                        clients: audience.notificationsOff,
                                                    },
                                                ]}
                                            />
                                        </div>
                                    ) : null}
                                    <div>
                                        <Text size="xs" muted className="mb-3">
                                            {t("admin.alumni.push.recipientsDescription")}
                                        </Text>
                                        {/* The list scrolls inside this, rather than the page growing with every alum. */}
                                        <div className="flex max-h-128 min-h-0 flex-col">
                                            <RecipientList
                                                recipients={audience.recipients}
                                                excluded={excluded}
                                                onToggle={toggle}
                                                loading={loading && !data}
                                                emptyMessage={t("admin.alumni.push.noRecipients")}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                </TabsContent>

                <TabsContent value="history" className="mt-0">
                    <PushNotificationHistory
                        notifications={historyData?.clientPushNotificationHistory ?? []}
                        loading={historyLoading}
                    />
                </TabsContent>
            </Tabs>

            {tab === "send" && (
                <div className="border-border bg-background sticky bottom-0 border-t">
                    <div className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                        <Text muted className="text-sm">
                            {t("admin.alumni.push.summary", { count: recipients })}
                        </Text>
                        <Button className="gap-2" disabled={!canSend} onClick={() => setConfirming(true)}>
                            <Send className="h-4 w-4" aria-hidden />
                            {t("admin.alumni.push.send", { count: recipients })}
                        </Button>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={confirming}
                onOpenChange={setConfirming}
                title={t("admin.alumni.push.confirmTitle")}
                description={confirmDescription}
                confirmLabel={t("admin.alumni.push.send", { count: recipients })}
                loadingLabel={t("admin.alumni.broadcast.sending")}
                loading={sending}
                onConfirm={send}
            />
        </div>
    );
}
